import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { Patient, Session, Appointment } from '../types';
import { Client, Session as MTSession, Appointment as MTAppointment } from '../types/multiTenant';
import { useClients } from '../hooks/useClients';
import { useSessions } from '../hooks/useSessions';
import { useAppointments } from '../hooks/useAppointments';

// =============================================================================
// TYPE TRANSFORMERS - Convert between old and new types for compatibility
// =============================================================================

/**
 * Map Portuguese gender values to English database values
 */
function mapGenderPtToEn(gender: string): 'male' | 'female' | 'other' | 'prefer_not_to_say' {
  const genderLower = (gender || '').toLowerCase().trim();

  if (genderLower === 'feminino' || genderLower === 'female' || genderLower === 'f') {
    return 'female';
  }
  if (genderLower === 'masculino' || genderLower === 'male' || genderLower === 'm') {
    return 'male';
  }
  if (genderLower === 'outro' || genderLower === 'other') {
    return 'other';
  }
  if (genderLower === 'prefiro não informar' || genderLower === 'prefer_not_to_say' || genderLower === 'nao informar') {
    return 'prefer_not_to_say';
  }

  // Default to 'other' if unknown
  return 'other';
}

/**
 * Map English gender values back to Portuguese for display
 */
function mapGenderEnToPt(gender: string): string {
  switch (gender) {
    case 'female': return 'Feminino';
    case 'male': return 'Masculino';
    case 'other': return 'Outro';
    case 'prefer_not_to_say': return 'Prefiro não informar';
    default: return gender || '';
  }
}

/**
 * Transform Client (multi-tenant) to Patient (legacy)
 *
 * Hardening (2026-05-20): defaults seguros para TODOS os campos opcionais.
 * Cliente novo reportou white-screen ao editar paciente importado de outra
 * plataforma — paciente entrou no banco com vários campos `null`/`undefined`
 * que não estavam previstos no schema legacy. Sem ErrorBoundary, qualquer
 * `.field` de undefined durante render quebrava a árvore React inteira.
 *
 * Regra: nunca retornar `undefined` de campo string; nunca passar `null` pra
 * `new Date()` (resulta em Invalid Date). Sempre devolver string vazia,
 * array vazio ou ISO string válida.
 */
function clientToPatient(client: Client): Patient {
  // birthDate pode ser '' (vazia), null, ou ISO string. Só calcula age se válida.
  const birthDateRaw = client.dateOfBirth || '';
  let age = 0;
  if (birthDateRaw) {
    const parsed = new Date(birthDateRaw);
    if (!isNaN(parsed.getTime())) {
      age = Math.max(0, new Date().getFullYear() - parsed.getFullYear());
    }
  }

  // firstConsultDate cai pra createdAt; createdAt cai pra now se também null
  // (não deveria, mas paranoia paga a longo prazo).
  const nowIso = new Date().toISOString();
  const createdAt = client.createdAt || nowIso;
  const updatedAt = client.updatedAt || createdAt;

  const customData = (client.customData as any) || {};

  return {
    id: client.id,
    name: client.fullName || '',
    birthDate: birthDateRaw,
    age,
    gender: mapGenderEnToPt(client.gender || ''),
    maritalStatus: customData.maritalStatus || '',
    firstConsultDate: createdAt,
    address: client.address || '',
    zipCode: client.zipCode || '',
    addressNumber: customData.addressNumber || '',
    neighborhood: customData.neighborhood || '',
    city: client.city || '',
    state: client.state || '',
    phone: client.phone || '',
    referencePhone: client.mobile || '',
    previousTreatmentHistory: client.notes || '',
    sessionPrice: client.sessionPrice,
    createdAt,
    updated_at: updatedAt,
    updatedAt,
    deleted: !!client.deleted,
    status: client.status || 'active',
    tags: Array.isArray(client.tags) ? client.tags : [],
  };
}

/**
 * Transform Patient (legacy) to Client (multi-tenant)
 */
function patientToClient(patient: Patient, organizationId: string, createdBy: string): Omit<Parameters<typeof import('../services/clientService').default.createClient>[0], 'organizationId' | 'createdBy'> {
  // Hardening 2026-05-24: import CSV de paciente sem data de nascimento
  // crashava silenciosamente. Supabase column `date_of_birth` é DATE — string
  // vazia '' NÃO é date válida, INSERT retorna 22007 e o front engolia o erro
  // em catch (log console + count em "erros"), mas a UI ainda fechava normal
  // dando impressão de sucesso. Quando faltam dados opcionais, mandar
  // `undefined` (que o supabase-js omite do INSERT, aceitando default/NULL).
  const normalizeOpt = (v: any) => {
    if (v === undefined || v === null) return undefined;
    if (typeof v === 'string' && v.trim() === '') return undefined;
    return v;
  };

  // Email tem mesma armadilha — se schema for NOT NULL, '' viola; se
  // nullable, melhor mandar undefined que '' (consistência).
  return {
    fullName: patient.name || '',
    dateOfBirth: normalizeOpt(patient.birthDate),
    gender: mapGenderPtToEn(patient.gender),
    email: normalizeOpt((patient as any).email) || '',
    phone: patient.phone || '',
    mobile: normalizeOpt(patient.referencePhone),
    address: normalizeOpt(patient.address),
    zipCode: normalizeOpt(patient.zipCode),
    city: normalizeOpt(patient.city),
    state: normalizeOpt(patient.state),
    country: 'Brazil',
    sessionPrice: patient.sessionPrice,
    currency: 'BRL',
    notes: normalizeOpt(patient.previousTreatmentHistory),
    customData: {
      maritalStatus: patient.maritalStatus,
      addressNumber: patient.addressNumber,
      neighborhood: patient.neighborhood,
    },
  };
}

/**
 * Transform multi-tenant Session to legacy Session
 *
 * Hardening (2026-05-20): defaults defensivos pra session com sessionDate null
 * (mesmo padrão do bug do PatientForm — paciente importado com session_price=null
 * fez white-screen). Aqui `mtSession.sessionDate.split('T')[0]` crasharia em null.
 */
function mtSessionToSession(mtSession: MTSession): Session {
  const sessionDateStr = mtSession.sessionDate || '';
  const sessionDate = sessionDateStr ? new Date(sessionDateStr) : null;
  const safeDate = sessionDate && !isNaN(sessionDate.getTime()) ? sessionDate : null;

  return {
    id: mtSession.id,
    patientId: mtSession.clientId,
    date: sessionDateStr.includes('T') ? sessionDateStr.split('T')[0] : sessionDateStr.substring(0, 10),
    time: safeDate ? safeDate.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : '',
    content: mtSession.content || '',
    attachments: Array.isArray(mtSession.attachments) ? mtSession.attachments.map((att: any) => ({
      id: att.id || String(Math.random()),
      sessionId: mtSession.id,
      name: att.name || att.filename || 'arquivo',
      type: att.type || att.mimeType || 'application/octet-stream',
      url: att.url || '',
      createdAt: att.createdAt || mtSession.createdAt || new Date().toISOString(),
    })) : [],
    createdAt: mtSession.createdAt || new Date().toISOString(),
    updated_at: mtSession.updatedAt || mtSession.createdAt || new Date().toISOString(),
    updatedAt: mtSession.updatedAt || mtSession.createdAt || new Date().toISOString(),
    deleted: !!mtSession.deleted,
  };
}

/**
 * Transform legacy Session to multi-tenant Session data
 */
function sessionToMTSession(session: Session, clientId: string, conductedBy: string): Omit<Parameters<typeof import('../services/sessionService').default.createSession>[0], 'organizationId' | 'createdBy'> {
  const sessionDateTime = `${session.date}T${session.time || '00:00'}:00`;

  return {
    clientId,
    conductedBy,
    sessionDate: sessionDateTime,
    content: session.content,
    attachments: session.attachments?.map(att => ({
      filename: att.name,
      mimeType: att.type,
      url: att.url,
      size: 0,
    })),
  };
}

/**
 * Transform multi-tenant Appointment to legacy Appointment
 */
function mtAppointmentToAppointment(mtAppointment: MTAppointment, clientName: string): Appointment {
  // Extrair hora diretamente da string sem conversão de timezone
  // startTime pode vir como "2025-12-05T07:00:00" ou "2025-12-05 07:00:00+00"
  let timePart = '00:00';
  let datePart = '';

  const startTimeStr = mtAppointment.startTime || '';
  if (startTimeStr.includes('T')) {
    datePart = startTimeStr.split('T')[0];
    timePart = startTimeStr.split('T')[1].substring(0, 5);
  } else if (startTimeStr.includes(' ')) {
    // Formato "2025-12-05 07:00:00+00"
    datePart = startTimeStr.split(' ')[0];
    timePart = startTimeStr.split(' ')[1].substring(0, 5);
  } else {
    datePart = startTimeStr.substring(0, 10);
  }

  return {
    id: mtAppointment.id,
    patientId: mtAppointment.clientId,
    patientName: clientName,
    date: datePart,
    time: timePart,
    notes: mtAppointment.notes,
    sessionPrice: mtAppointment.price,
    status: mtAppointment.status as 'scheduled' | 'completed' | 'cancelled',
    attendanceStatus: mtAppointment.attendanceStatus as 'present' | 'absent' | null,
    deleted: mtAppointment.deleted,
    created_at: mtAppointment.createdAt,
    updated_at: mtAppointment.updatedAt,
    createdAt: mtAppointment.createdAt,
    updatedAt: mtAppointment.updatedAt,
    location: mtAppointment.location,
    videoCallUrl: mtAppointment.videoCallUrl,
  };
}

/**
 * Transform legacy Appointment to multi-tenant Appointment data
 */
function appointmentToMTAppointment(appointment: Appointment, assignedTo: string): Omit<Parameters<typeof import('../services/appointmentService').default.createAppointment>[0], 'organizationId' | 'createdBy'> {
  const timeStr = appointment.time || '00:00';
  const startDateTime = `${appointment.date}T${timeStr}:00`;

  // Calcular end time mantendo no mesmo formato (sem conversão UTC)
  const [hours, minutes] = timeStr.split(':').map(Number);
  const endHours = (hours + 1) % 24;
  const endDate = hours === 23 ? appointment.date : appointment.date; // Simplificado - assumindo que sessão não passa de meia-noite
  const endDateTime = `${endDate}T${endHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`;

  return {
    clientId: appointment.patientId,
    assignedTo,
    startTime: startDateTime,
    endTime: endDateTime,
    durationMinutes: 60,
    title: `Consulta - ${appointment.patientName}`,
    notes: appointment.notes,
    price: appointment.sessionPrice,
    currency: 'BRL',
    status: appointment.status || 'scheduled',
    location: appointment.location,
    videoCallUrl: appointment.videoCallUrl,
  };
}

// =============================================================================
// CONTEXT DEFINITION
// =============================================================================

interface RealtimeDataContextType {
  // Data
  patients: Patient[];
  sessions: Record<string, Session[]>;
  appointments: Appointment[];

  // State
  isLoading: boolean;
  error: string | null;
  isInitialized: boolean;
  isOnline: boolean;

  // Actions - Patients
  savePatient: (patient: Patient) => Promise<void>;
  deletePatient: (patientId: string) => Promise<void>;

  // Actions - Sessions
  addSession: (session: Session) => Promise<void>;
  updateSession: (session: Session) => Promise<void>;
  deleteSession: (patientId: string, sessionId: string) => Promise<void>;
  getSessionsForPatient: (patientId: string) => Session[];

  // Actions - Appointments
  saveAppointment: (appointment: Appointment) => Promise<void>;
  deleteAppointment: (appointmentId: string) => Promise<void>;

  // Utilities
  forceSync: () => Promise<void>;
  forceFullSync: () => Promise<void>;
  forceUploadLocalData: () => Promise<void>;
  forceDownloadSupabaseData: () => Promise<void>;
  getStats: () => { patients: number; appointments: number; sessions: number };
  cleanupDuplicates: () => Promise<string>;
  clearError: () => void;

  // Recovery functions
  recoverFromBackup: (backupId: string) => Promise<boolean>;
  getAvailableBackups: () => any[];
  emergencyRecovery: () => Promise<boolean>;
}

const RealtimeDataContext = createContext<RealtimeDataContextType | undefined>(undefined);

// =============================================================================
// PROVIDER IMPLEMENTATION
// =============================================================================

interface RealtimeDataProviderProps {
  children: React.ReactNode;
}

export function RealtimeDataProvider({ children }: RealtimeDataProviderProps) {
  // Use new multi-tenant hooks
  const clientsHook = useClients();
  const sessionsHook = useSessions();
  const appointmentsHook = useAppointments();

  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [contextError, setContextError] = useState<string | null>(null);

  // Monitor network status
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      console.log('Application went online');
    };

    const handleOffline = () => {
      setIsOnline(false);
      console.log('Application went offline');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Transform clients to patients
  const patients = useMemo(() => {
    return clientsHook.clients.map(clientToPatient);
  }, [clientsHook.clients]);

  // Group sessions by patient ID
  const sessions = useMemo(() => {
    const grouped: Record<string, Session[]> = {};

    sessionsHook.sessions.forEach((mtSession) => {
      const session = mtSessionToSession(mtSession);
      const patientId = session.patientId;

      if (!grouped[patientId]) {
        grouped[patientId] = [];
      }
      grouped[patientId].push(session);
    });

    return grouped;
  }, [sessionsHook.sessions]);

  // Transform appointments with client names
  const appointments = useMemo(() => {
    return appointmentsHook.appointments.map((mtAppointment) => {
      const client = clientsHook.clients.find(c => c.id === mtAppointment.clientId);
      const clientName = client?.fullName || 'Unknown';
      return mtAppointmentToAppointment(mtAppointment, clientName);
    });
  }, [appointmentsHook.appointments, clientsHook.clients]);

  // Combined loading state
  const isLoading = clientsHook.isLoading || sessionsHook.isLoading || appointmentsHook.isLoading;

  // Combined error state
  const error = contextError || clientsHook.error || sessionsHook.error || appointmentsHook.error;

  // Initialized when all data is loaded at least once
  const isInitialized = !isLoading && patients.length >= 0;

  // =============================================================================
  // ACTIONS - Patient/Client
  // =============================================================================

  const savePatient = useCallback(async (patient: Patient) => {
    setContextError(null);
    try {
      if (patient.id && clientsHook.clients.find(c => c.id === patient.id)) {
        // Update existing client
        const updates = patientToClient(patient, '', '');
        await clientsHook.updateClient(patient.id, updates as any);
      } else {
        // Create new client
        const clientData = patientToClient(patient, '', '');
        await clientsHook.createClient(clientData);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save patient';
      setContextError(errorMessage);
      throw err;
    }
  }, [clientsHook]);

  const deletePatient = useCallback(async (patientId: string) => {
    setContextError(null);
    try {
      await clientsHook.deleteClient(patientId);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete patient';
      setContextError(errorMessage);
      throw err;
    }
  }, [clientsHook]);

  // =============================================================================
  // ACTIONS - Sessions
  // =============================================================================

  const addSession = useCallback(async (session: Session) => {
    setContextError(null);
    try {
      // For new sessions, we need conductedBy from current user
      // This would come from OrganizationContext in a real scenario
      const sessionData = sessionToMTSession(session, session.patientId, '');
      await sessionsHook.createSession(sessionData as any);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to add session';
      setContextError(errorMessage);
      throw err;
    }
  }, [sessionsHook]);

  const updateSession = useCallback(async (session: Session) => {
    setContextError(null);
    try {
      const updates: any = {
        content: session.content,
        attachments: session.attachments?.map(att => ({
          id: att.id,
          name: att.name,
          type: att.type,
          url: att.url,
          size: 0,
        })),
      };
      await sessionsHook.updateSession(session.id, updates);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update session';
      setContextError(errorMessage);
      throw err;
    }
  }, [sessionsHook]);

  const deleteSession = useCallback(async (patientId: string, sessionId: string) => {
    setContextError(null);
    try {
      await sessionsHook.deleteSession(sessionId);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete session';
      setContextError(errorMessage);
      throw err;
    }
  }, [sessionsHook]);

  const getSessionsForPatient = useCallback((patientId: string): Session[] => {
    return sessions[patientId] || [];
  }, [sessions]);

  // =============================================================================
  // ACTIONS - Appointments
  // =============================================================================

  const saveAppointment = useCallback(async (appointment: Appointment) => {
    // NOTA: não chamamos setContextError aqui porque o wrapper [pages/Calendar.tsx](pages/Calendar.tsx)
    // trata qualquer `error` do contexto como "erro de carregamento" e esconde a UI inteira.
    // Erros de save devem ser tratados pelo caller (Calendar component) via snackbar — apenas
    // lançamos para o caller capturar.
    try {
      if (appointment.id && appointmentsHook.appointments.find(a => a.id === appointment.id)) {
        // Update existing - convert status
        const status = appointment.status === 'cancelled' ? 'canceled' : appointment.status;
        // Para resetar attendanceStatus para null, precisamos passar null explicitamente
        // (Supabase ignora campos com undefined, mas atualiza campos com null)
        const updated = await appointmentsHook.updateAppointment(appointment.id, {
          notes: appointment.notes,
          status: status as any,
          attendanceStatus: appointment.attendanceStatus as any, // null reseta o campo
        });
        if (!updated) {
          throw new Error('Não foi possível atualizar a consulta. Tente novamente.');
        }
      } else {
        // Create new - assignedTo would come from current user
        const appointmentData = appointmentToMTAppointment(appointment, '');
        const created = await appointmentsHook.createAppointment(appointmentData as any);
        if (!created) {
          // Defesa em profundidade: useAppointments deve lançar erro nesse caso,
          // mas garantimos aqui também que ninguém engula uma falha silenciosa.
          throw new Error('Não foi possível salvar a consulta. Tente novamente.');
        }
      }
    } catch (err) {
      // Não setamos contextError (causaria pages/Calendar.tsx a mostrar tela de erro de load).
      // Apenas relançamos para o caller exibir snackbar via seu try/catch.
      throw err;
    }
  }, [appointmentsHook]);

  const deleteAppointment = useCallback(async (appointmentId: string) => {
    // Mesmo motivo do saveAppointment: erro de ação não deve hijack o contextError
    // (que sinaliza erro de load). Só relançamos para o caller mostrar snackbar.
    try {
      await appointmentsHook.deleteAppointment(appointmentId);
    } catch (err) {
      throw err;
    }
  }, [appointmentsHook]);

  // =============================================================================
  // UTILITIES
  // =============================================================================

  const forceSync = useCallback(async () => {
    setContextError(null);
    try {
      await Promise.all([
        clientsHook.refresh(),
        sessionsHook.refresh(),
        appointmentsHook.refresh(),
      ]);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to sync';
      setContextError(errorMessage);
      throw err;
    }
  }, [clientsHook, sessionsHook, appointmentsHook]);

  const forceFullSync = useCallback(async () => {
    // Same as forceSync in new architecture
    return forceSync();
  }, [forceSync]);

  const forceUploadLocalData = useCallback(async () => {
    // Not applicable in new architecture - data is always in Supabase
    console.warn('forceUploadLocalData is deprecated in multi-tenant architecture');
  }, []);

  const forceDownloadSupabaseData = useCallback(async () => {
    // Same as forceSync in new architecture
    return forceSync();
  }, [forceSync]);

  const getStats = useCallback(() => {
    return {
      patients: patients.length,
      appointments: appointments.length,
      sessions: Object.values(sessions).flat().length,
    };
  }, [patients, appointments, sessions]);

  const cleanupDuplicates = useCallback(async () => {
    // Not applicable in new architecture - Supabase handles uniqueness
    console.warn('cleanupDuplicates is deprecated in multi-tenant architecture');
    return 'No duplicates found';
  }, []);

  const clearError = useCallback(() => {
    setContextError(null);
  }, []);

  const recoverFromBackup = useCallback(async (backupId: string) => {
    // Not applicable in new architecture
    console.warn('recoverFromBackup is deprecated in multi-tenant architecture');
    return false;
  }, []);

  const getAvailableBackups = useCallback(() => {
    // Not applicable in new architecture
    console.warn('getAvailableBackups is deprecated in multi-tenant architecture');
    return [];
  }, []);

  const emergencyRecovery = useCallback(async () => {
    // In new architecture, just refresh all data
    try {
      await forceSync();
      return true;
    } catch (err) {
      return false;
    }
  }, [forceSync]);

  // Context value
  const contextValue: RealtimeDataContextType = useMemo(() => ({
    // Data
    patients,
    sessions,
    appointments,

    // State
    isLoading,
    error,
    isInitialized,
    isOnline,

    // Actions - Patients
    savePatient,
    deletePatient,

    // Actions - Sessions
    addSession,
    updateSession,
    deleteSession,
    getSessionsForPatient,

    // Actions - Appointments
    saveAppointment,
    deleteAppointment,

    // Utilities
    forceSync,
    forceFullSync,
    forceUploadLocalData,
    forceDownloadSupabaseData,
    getStats,
    cleanupDuplicates,
    clearError,

    // Recovery
    recoverFromBackup,
    getAvailableBackups,
    emergencyRecovery,
  }), [
    patients, sessions, appointments,
    isLoading, error, isInitialized, isOnline,
    savePatient, deletePatient,
    addSession, updateSession, deleteSession, getSessionsForPatient,
    saveAppointment, deleteAppointment,
    forceSync, forceFullSync, forceUploadLocalData, forceDownloadSupabaseData,
    getStats, cleanupDuplicates, clearError,
    recoverFromBackup, getAvailableBackups, emergencyRecovery,
  ]);

  return (
    <RealtimeDataContext.Provider value={contextValue}>
      {children}
    </RealtimeDataContext.Provider>
  );
}

// =============================================================================
// HOOKS
// =============================================================================

/**
 * Main hook to use the realtime data context
 */
export function useRealtimeDataContext(): RealtimeDataContextType {
  const context = useContext(RealtimeDataContext);

  if (context === undefined) {
    throw new Error('useRealtimeDataContext must be used within RealtimeDataProvider');
  }

  return context;
}

/**
 * Hook to get only data (without actions)
 */
export function useRealtimeDataRead() {
  const { patients, sessions, appointments, isLoading, error, isInitialized } = useRealtimeDataContext();

  return {
    patients,
    sessions,
    appointments,
    isLoading,
    error,
    isInitialized
  };
}

/**
 * Hook to get sessions for a specific patient
 */
export function usePatientSessions(patientId: string) {
  const { getSessionsForPatient, isLoading, error } = useRealtimeDataContext();

  return {
    sessions: getSessionsForPatient(patientId),
    isLoading,
    error
  };
}

/**
 * Hook to get only actions
 */
export function useRealtimeDataActions() {
  const {
    savePatient,
    deletePatient,
    addSession,
    updateSession,
    deleteSession,
    saveAppointment,
    deleteAppointment,
    forceSync,
    forceFullSync,
    forceUploadLocalData,
    forceDownloadSupabaseData,
    getStats,
    clearError,
    recoverFromBackup,
    getAvailableBackups,
    emergencyRecovery
  } = useRealtimeDataContext();

  return {
    savePatient,
    deletePatient,
    addSession,
    updateSession,
    deleteSession,
    saveAppointment,
    deleteAppointment,
    forceSync,
    forceFullSync,
    forceUploadLocalData,
    forceDownloadSupabaseData,
    getStats,
    clearError,
    recoverFromBackup,
    getAvailableBackups,
    emergencyRecovery
  };
}

/**
 * Hook to get status information
 */
export function useRealtimeDataStatus() {
  const { isLoading, error, isInitialized, isOnline, clearError } = useRealtimeDataContext();

  return {
    isLoading,
    error,
    isInitialized,
    isOnline,
    clearError
  };
}

/**
 * Hook to get data statistics
 */
export function useDataStatistics() {
  const { getStats, isLoading, error } = useRealtimeDataContext();

  return {
    stats: getStats(),
    isLoading,
    error
  };
}
